import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, ExternalLinkIcon, MapPinIcon, MessageSquareIcon, PhoneIcon, ShieldCheckIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { LOCATIONS, OPENING_HOURS, PHONE, SERVICE_MENUS } from '../data/locations';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/8ca803bb-c935-40b0-9f97-dd773b5fdb7f.jpg';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function Book() {
  const [searchParams] = useSearchParams();
  const initialSlug = searchParams.get('location') ?? '';
  const [slug, setSlug] = useState(LOCATIONS.some(l => l.slug === initialSlug) ? initialSlug : '');
  const [menuName, setMenuName] = useState(SERVICE_MENUS[0].name);
  const [tierDuration, setTierDuration] = useState(SERVICE_MENUS[0].tiers[0].duration);
  const location = LOCATIONS.find(l => l.slug === slug);
  const menu = SERVICE_MENUS.find(m => m.name === menuName) ?? SERVICE_MENUS[0];
  const tier = menu.tiers.find(t => t.duration === tierDuration) ?? menu.tiers[0];
  const smsBody = location ? `Hi ${location.name}, I'd like to book a ${menu.name} (${tier.duration}). Sent via the Me Time website.` : '';
  return <main className="w-full bg-black" data-id="element-124" data-mp="17r32kijzha5q">
      <PageHero image={HERO_IMAGE} eyebrow="Reserve" title="Book Your Session" subtitle="Choose a studio and treatment, then pick a time that suits you." icon={<CalendarIcon className="h-6 w-6" aria-hidden="true" data-id="element-126" data-mp="1hka4ftfim812" />} data-id="element-125" data-mp="12tks2nqbimvm" />

      <section aria-label="Booking" className="w-full bg-black" data-id="element-127" data-mp="1cjw4yzrzc6en">
        <div className="mx-auto w-full max-w-4xl space-y-6 px-5 py-20 sm:px-8" data-id="element-128" data-mp="1qidtgzpplmxm">
          <fieldset className="rounded-2xl border border-white/5 bg-ink-900 p-6 sm:p-8" data-id="element-129" data-mp="1lkc5q1hc8zft">
            <legend className="px-2 text-xs font-semibold uppercase tracking-[0.24em] text-lotus-500" data-id="element-130" data-mp="j6mo1pfv1a0j">
              Step 1 — Location
            </legend>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-131" data-mp="o691nzzlos9v">
              {LOCATIONS.map(loc => {
              const active = slug === loc.slug;
              return <label key={loc.slug} className={`cursor-pointer rounded-xl border p-5 transition-colors ${active ? 'border-lotus-500 bg-lotus-500/10' : 'border-white/5 bg-ink-850 hover:border-white/20'}`} data-id="element-132" data-mp="wz2vr3jphw7s">
                    <input type="radio" name="location" value={loc.slug} checked={active} onChange={() => setSlug(loc.slug)} className="sr-only" data-id="element-133" data-mp="1fwx6bm0hgfsa" />
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-134" data-mp="3a4r8ck9oxzou">
                      {loc.suburb}
                    </span>
                    <span className="mt-1.5 block font-bold text-white" data-id="element-135" data-mp="35by19pqr2f9l">{loc.name}</span>
                    <span className="mt-2 flex items-start gap-2 text-sm text-neutral-400" data-id="element-136" data-mp="30f7md5qolppr">
                      <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-137" data-mp="2wexj40u9xdel" />
                      {loc.address}
                    </span>
                  </label>;
            })}
            </div>
          </fieldset>

          <fieldset className="rounded-2xl border border-white/5 bg-ink-900 p-6 sm:p-8" data-id="element-138" data-mp="3k2ou9142vkea">
            <legend className="px-2 text-xs font-semibold uppercase tracking-[0.24em] text-lotus-500" data-id="element-139" data-mp="3f3ub1y277ttn">
              Step 2 — Treatment
            </legend>
            <div className="mt-4 space-y-5" data-id="element-140" data-mp="28pcxfa6qk21m">
              {SERVICE_MENUS.map(m => <div key={m.name} data-id="element-141" data-mp="23nvyvlbq4ro7">
                  <p className="text-sm font-semibold text-white" data-id="element-142" data-mp="23r1msx8m9nic">{m.name}</p>
                  <div className="mt-3 flex flex-wrap gap-3" data-id="element-143" data-mp="3rk24wa7ur4xd">
                    {m.tiers.map(t => {
                  const active = menuName === m.name && tierDuration === t.duration;
                  return <label key={`${m.name}-${t.duration}`} className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${active ? 'border-lotus-500 bg-lotus-500 text-black' : 'border-white/10 bg-ink-850 text-neutral-300 hover:border-white/25'}`} data-id="element-144" data-mp="3iivnlyxdpq32">
                          <input type="radio" name="treatment" checked={active} onChange={() => {
                      setMenuName(m.name);
                      setTierDuration(t.duration);
                    }} className="sr-only" data-id="element-145" data-mp="a6zre91d3c7h" />
                          {t.duration} — {t.price}
                        </label>;
                })}
                  </div>
                </div>)}
            </div>
          </fieldset>

          <div className="rounded-2xl border border-white/5 bg-ink-900 p-6 sm:p-8" data-id="element-146" data-mp="23muclcsjn2ku">
            <h2 className="text-xs font-semibold uppercase tracking-[0.24em] text-lotus-500" data-id="element-147" data-mp="1yosqeiztmn55">
              Step 3 — Pick your time
            </h2>

            {!location ? <p className="mt-4 rounded-xl border border-white/5 bg-ink-850 p-6 text-center text-sm text-neutral-400" data-id="element-148" data-mp="2df8cs13z414w">
                Choose a location above to see live availability.
              </p> : <motion.div key={location.slug} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeOut'
          }} className="mt-5" data-id="element-149" data-mp="1fyjhkq9j3x4h">
                <dl className="grid gap-3 rounded-xl border border-white/5 bg-ink-850 p-5 sm:grid-cols-3" data-id="element-150" data-mp="3kx119brw70n7">
                  <div data-id="element-151" data-mp="3tro3y0668yvr">
                    <dt className="text-xs uppercase tracking-wider text-neutral-500" data-id="element-152" data-mp="3ts7egvthskzr">Venue</dt>
                    <dd className="mt-1 text-sm font-semibold text-white" data-id="element-153" data-mp="2x1yb90hcqr55">{location.name}</dd>
                  </div>
                  <div data-id="element-154" data-mp="321jc5gg319ki">
                    <dt className="text-xs uppercase tracking-wider text-neutral-500" data-id="element-155" data-mp="3211fiipictc2">
                      Treatment
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-white" data-id="element-156" data-mp="3b0y2mfw91k50">
                      {menu.name} · {tier.duration}
                    </dd>
                  </div>
                  <div data-id="element-157" data-mp="le8gxpqvgrk5">
                    <dt className="text-xs uppercase tracking-wider text-neutral-500" data-id="element-158" data-mp="qap52jy2n5x8">Price</dt>
                    <dd className="mt-1 text-sm font-semibold text-lotus-500" data-id="element-159" data-mp="10emdiqjj1h19">{tier.price}</dd>
                  </div>
                </dl>

                {location.squareBookingUrl ? <>
                    <a href={location.squareBookingUrl} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-lotus-500 px-8 py-4 text-base font-semibold text-black transition-colors hover:bg-lotus-600" data-id="element-160" data-mp="qokljno1zeh2">
                      Choose a time &amp; book
                      <ExternalLinkIcon className="h-5 w-5" aria-hidden="true" data-id="element-161" data-mp="7r00kroet01q" />
                    </a>
                    <p className="mt-4 flex items-center justify-center gap-2 text-xs text-neutral-500" data-id="element-162" data-mp="3q7n1m8i88zn">
                      <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-163" data-mp="223gco93vz8ei" />
                      Live availability and instant confirmation, powered by Square Appointments.
                    </p>
                  </> : <div className="mt-5 rounded-xl border border-white/5 bg-ink-850 p-6 text-center" data-id="element-164" data-mp="2lcrazoz1g9pk">
                    <p className="text-sm text-neutral-300" data-id="element-165" data-mp="27luj60cxrwjk">
                      Online booking is coming soon for this venue. Text or call{' '}
                      {location.name} to lock in a time — they answer during opening hours.
                    </p>
                    <div className="mt-5 flex flex-col gap-3 sm:flex-row" data-id="element-166" data-mp="1xpj81dp31cqp">
                      <a href={`sms:${toIntlNumber(location.phone)}?&body=${encodeURIComponent(smsBody)}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-lotus-500 px-6 py-3.5 font-semibold text-black transition-colors hover:bg-lotus-600" data-id="element-167" data-mp="22nb1ic5dl6x0">
                        <MessageSquareIcon className="h-5 w-5" aria-hidden="true" data-id="element-168" data-mp="3nyjhni5t6qiv" />
                        Text this venue
                      </a>
                      <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-white/10 bg-ink-900 px-6 py-3.5 font-semibold text-white transition-colors hover:border-white/25" data-id="element-169" data-mp="3t4omhxh9br2v">
                        <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-170" data-mp="kntrf46pbazc" />
                        Call {location.phone}
                      </a>
                    </div>
                  </div>}
              </motion.div>}

            <p className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-500" data-id="element-171" data-mp="tn8fjwzeb9go">
              <span className="flex items-center gap-2" data-id="element-172" data-mp="2vgo7qyb4swdr">
                <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-173" data-mp="2vh7s0lo97wuy" />
                {OPENING_HOURS}
              </span>
              <span className="flex items-center gap-2" data-id="element-174" data-mp="2rhp5jlgsbdq0">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-175" data-mp="2pg76ihmc76le" />
                Prefer to call?{' '}
                <a href={`tel:${(location ? location.phone : PHONE).replace(/\s/g, '')}`} className="text-lotus-500 hover:text-lotus-600" data-id="element-176" data-mp="2ld0zh7rehbdv">
                  {location ? location.phone : PHONE}
                </a>
              </span>
            </p>
          </div>
        </div>
      </section>
    </main>;
}